package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.enginetabular.META_HEADERS
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Shared sales overview for both lead sources. */
@Composable
fun Leaduebersicht() {
    val scope = rememberCoroutineScope()
    val sharedKpis = LocalLinkedInKpis.current
    var leads by remember { mutableStateOf<List<All>>(emptyList()) }
    var additional by remember { mutableStateOf<List<VertriebsindexApi.ZusatzHeader>>(emptyList()) }
    var checkpoint by remember { mutableStateOf("") }
    var selected by remember { mutableStateOf<Set<String>>(emptySet()) }
    var infoId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }

    suspend fun load() {
        loading = true
        try {
            val data = LinkedInFlowApi.overview()
            leads = data.linkedinKontakte.filter {
                it.linkedinProspectId.isNotBlank() && salesCheckpoints.indexOfFirst { phase -> phase.id == it.vertriebCheckpoint } >= 1
            }.distinctBy { "${it.leadQuelle}:${it.linkedinProspectId}" }
            additional = VertriebsindexApi.loadHeaders().filter { it.typ != "CHECKBOX" && linkedInOverviewField(it.id, it.label) }
            error = ""
        } catch (e: CancellationException) { throw e }
        catch (e: Exception) { error = e.message ?: "LinkedIn-Leadübersicht konnte nicht geladen werden." }
        finally { loading = false }
    }
    LaunchedEffect(LinkedInSyncState.version) { load() }
    val activeCheckpoint = sharedKpis?.selected ?: checkpoint
    val currentLeads = sharedKpis?.contacts?.takeIf { it.isNotEmpty() }?.filter {
        it.linkedinProspectId.isNotBlank() && salesCheckpoints.indexOfFirst { phase -> phase.id == it.vertriebCheckpoint } >= 1
    } ?: leads
    val visible = currentLeads.filter { activeCheckpoint.isBlank() || checkpointMatches(it, activeCheckpoint) }
    LaunchedEffect(activeCheckpoint) { selected = emptySet() }
    val columns = listOf(
        "vertriebCheckpoint", "leadQuelle", "leadTyp", "linkedinNetzwerkstatus", "nachname", "vorname", "ansprechpartner", "firma",
        "strasse", "hausnummer", "plz", "ort", "google_maps", "webseite", "ansprechpartnerPosition", "email", "linkedin", "telefon", "mobil",
        "linkedinVernetztAm", "vertriebFollowUpFaellig",
        "akquiseCallAm", "akquiseTerminAm", "vertriebAbschlussErgebnis", "vertriebAbschlussGrund"
    )
    val labels = listOf(
        "Zustand", "Leadquelle", "Lead Typ", "Netzwerkstatus", "Nachname", "Vorname", "Ansprechpartner", "Firma",
        "Straße", "Hausnummer", "PLZ", "Ort", "Google Maps", "Webseite", "Position", "E-Mail", "LinkedIn", "Telefon", "Mobil",
        "Vernetzt seit", "Follow-up",
        "Call-Termin", "Termin", "Abschlussergebnis", "Abschlussgrund"
    )
    fun date(value: String): String = if (value.isBlank()) "" else {
        val parsed = kotlin.js.Date(value)
        if (parsed.getTime().isNaN()) value else parsed.toLocaleString("de-DE")
    }
    fun cells(lead: All) = listOf(
        checkpointLabel(lead.vertriebCheckpoint), lead.leadQuelle.ifBlank { "LinkedIn" }, lead.leadTyp, lead.linkedinNetzwerkstatus,
        lead.nachname, lead.vorname, lead.ansprechpartner, lead.firma, lead.strasse, lead.hausnummer, lead.plz, lead.ort,
        vertriebGoogleMapsUrl(lead), lead.webseite, lead.ansprechpartnerPosition, lead.email, lead.linkedin, lead.telefon, lead.mobil,
        linkedInConnectedDate(lead.linkedinVernetztAm), if (lead.vertriebFollowUpFaellig) "Ja" else "Nein",
        date(lead.akquiseCallAm), date(lead.akquiseTerminAm),
        lead.vertriebAbschlussErgebnis, lead.vertriebAbschlussGrund
    ) + additional.map { lead.vertriebZusatzfelder[it.id].orEmpty() }
    val selectedLead = visible.singleOrNull { it.id in selected }
    currentLeads.firstOrNull { it.id == infoId }?.let { lead ->
        LeadInfoReadOnlyPopup(lead.copy(id = lead.linkedinProspectId)) { infoId = null }
    }
    Div({ style { height(100.percent); minHeight(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        if (sharedKpis == null) {
        SalesCheckpointTileRow {
            SalesCheckpointTile(SalesCheckpoint("ALL", "Alle Leads", "person"), leads.size, leads.count { it.vertriebFollowUpFaellig },
                checkpoint.isBlank(), !loading, "Alle LinkedIn-Leads ab Angeschrieben") { checkpoint = ""; selected = emptySet() }
            visibleSalesCheckpoints.filter { it.id != "MY_LEADS" }.forEach { phase ->
                val members = leads.filter { checkpointMatches(it, phase.id) }
                SalesCheckpointTile(phase, members.size, if (checkpointHasFollowUp(phase.id)) members.count { it.vertriebFollowUpFaellig } else null,
                    checkpoint == phase.id, !loading, phase.label) {
                    checkpoint = if (checkpoint == phase.id) "" else phase.id
                    selected = emptySet()
                }
            }
        }
        Div({ style { height(1.px); flexShrink(0); backgroundColor(Color("rgba(255,255,255,.14)")) } })
        }
        if (error.isNotBlank()) Div({ attr("role", "alert") }) { Text(error) }
        Div({ style { flex("1"); minHeight(0.px) } }) {
            TabularScreen(
                screenKey = "Leaduebersicht", rollenKey = "Leaduebersicht", title = "Leadübersicht",
                headers = columns + additional.map { it.id },
                headerDisplayLabels = labels + additional.map { it.label },
                fixedLeadingColumns = listOf("vertriebCheckpoint"), fixedTrailingColumns = META_HEADERS,
                models = visible, data = visible.map { it.id to cells(it) },
                editable = false, hardReadOnly = true, allowCreate = false, allowDelete = false,
                allowSave = false, allowImportButton = false, onSelectedRowsChange = { selected = it },
                extraNavButtons = listOf(
                    TabularToolbarButton("Lead-Info", "images/info.svg", selectedLead != null, onClick = { infoId = selectedLead?.id }),
                    TabularToolbarButton("Im Flow öffnen", "images/linkedin.svg",
                        selectedLead != null && RollenService.hasReadPermission("LinkedInFlow"), onClick = {
                            selectedLead?.let {
                                val id = js("encodeURIComponent")(it.id) as String
                                window.location.hash = window.location.hash.substringBefore('?') + "?screen=SYSVertriebsDashboard&linkedinContact=$id"
                            }
                        })
                ),
                onRefresh = { scope.launch { load() } }, allowSearchButton = true, showFilterRow = true, onCommit = {}
            )
        }
    }
}

private fun linkedInOverviewField(id: String, label: String): Boolean {
    val excluded = setOf("hallen", "tore", "regale", "regalplaetze", "anzahlhallen", "anzahltore", "anzahlregale", "anzahlregalplaetze")
    fun key(value: String) = value.lowercase().replace("ä", "ae").filter { it.isLetterOrDigit() }
    return key(id) !in excluded && key(label) !in excluded
}
